/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type RefObject,
} from "react";
import {
  mergeProps,
  useFocusRing,
  useId,
  useRadio,
  useRadioGroup,
  type AriaRadioGroupProps,
} from "react-aria";
import {
  useRadioGroupState as useGroupState,
  type RadioGroupState as AriaRadioGroupState,
} from "react-stately";

import { cssFns } from "@superweb/css";

import { FieldErrorMessage } from "../fields/error-message";
import { FieldDescription } from "../fields/description";
import { ToggleLabelContent } from "../fields/toggle-label-content";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { icons } from "../icons";
import { useMessage } from "#intl";

type InvalidRules = {
  required: boolean;
};

export type RadioGroupState<Value extends string> = {
  /**
   * Current value of radio group, that is value of the checked radio button.
   */
  value?: Value;

  /**
   * When set `false` the error message is not visible even if is set.
   * Is set on change depending on the interaction.
   * Can be set externally to force hide/show the error message.
   * The radio group has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorVisible?: boolean;

  /**
   * The error message associated with the radio group.
   * Visible only when `errorVisible` is `true`.
   * The radio group has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorMessage?: string;

  /**
   * Is set to `true` on change if the field contains invalid value.
   */
  invalid?: boolean;
};

type RadioButton<Value> = {
  /**
   * Radio button value.
   */
  value: Value;

  /**
   * Radio button label.
   */
  label: string;

  /**
   * Radio button description
   */
  description?: string;

  /**
   * Radio button icon
   */
  icon?: ReactElement;
};

export const RadioGroup = <Value extends string>({
  label,
  ariaLabel,
  description,
  options,
  readOnly = false,
  disabled = false,
  state,
  onChange,
  variant = "fill",
  labelAlign = "start",
  required = false,
  validate,
}: (
  | {
      /**
       * Text for group's label, that describes group's meaning.
       * The label is used for the accessibility of the element.
       *
       * Links:
       * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label
       */
      label: string;

      ariaLabel?: never;
    }
  | {
      label?: never;

      /**
       * Gives the group an accessible name.
       * Need to specify when the 'label' is not specified.
       * The requirements for the text in 'ariaLabel' are similar to prop 'label'.
       *
       * Links:
       * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
       */
      ariaLabel: string;
    }
) & {
  /**
   * Radio group description.
   * Provides a hint such as specific requirements for what to choose.
   */
  description?: string;

  /**
   * RadioGroup's options, that makes up a list of radio buttons.
   */
  options: RadioButton<Value>[];

  /**
   * Whether radio group is readonly (can be selected but not changed by the user).
   * @defaultValue false
   */
  readOnly?: boolean;

  /**
   * Whether full radio group is disabled.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * Current state of radio group.
   * The state stores the interactive fields, that change when interacting with the component.
   */
  state: RadioGroupState<Value>;

  /**
   * Handler that is called when the state value changes.
   * @param state - Recommended state for the component after the change.
   * @param info - Additional information about the validation state.
   */
  onChange: (
    state: RadioGroupState<Value>,
    info: {
      invalid: {
        /**
         * If `true`, the value is missing, but it's required.
         */
        required: boolean;
      };
    },
  ) => void;

  /**
   * Variant of radiogroup appearance.
   *
   * @defaultValue 'fill'
   */
  variant?: "fill" | "ghost";

  /**
   * Radiogroup label alignment.
   * @defaultValue 'start'
   */
  labelAlign?: "start" | "end";

  /**
   * Whether the radio group is required.
   * @defaultValue false
   */
  required?: boolean;

  /**
   * Custom validation function.
   * Called during validation to provide additional field validation beyond built-in rules.
   * Custom validation error messages take precedence over built-in error messages.
   *
   * @param state - Current RadioGroup state.
   * @returns Error message to display when invalid, or `undefined` when valid.
   */
  validate?: (state: RadioGroupState<Value>) => string | undefined;
}) => {
  const uiColors = useUiColors();
  const typo = useTypo();
  const getErrorMessage = useErrorMessage();

  const handleValidate = (currentState: RadioGroupState<Value>) => {
    const invalidRules = validateRadioGroup({
      value: currentState.value,
      required,
    });
    const customErrorMessage = validate?.(currentState);
    const errorMessage = Object.values(invalidRules).some(Boolean)
      ? getErrorMessage(invalidRules)
      : undefined;

    return {
      invalidRules: {
        ...invalidRules,
        custom: Boolean(customErrorMessage),
      },
      errorMessage: customErrorMessage || errorMessage,
    };
  };

  const handleChange = (currentState: RadioGroupState<Value>) => {
    const { invalidRules, errorMessage } = handleValidate(currentState);
    const invalid = Object.values(invalidRules).some(Boolean);

    onChange(
      {
        value: currentState.value,
        errorVisible: invalid,
        errorMessage,
        invalid,
      },
      {
        invalid: { required: invalidRules.required },
      },
    );
  };

  const groupProps: AriaRadioGroupProps = {
    label,
    "aria-label": ariaLabel,
    description,
    isRequired: required,
    errorMessage: state.errorMessage,
    value: state.value ?? null,
    validationState:
      state.errorVisible && state.errorMessage ? "invalid" : "valid",
    isReadOnly: readOnly,
    isDisabled: disabled,
    onChange: (key) => {
      handleChange({
        ...state,
        value: key as Value,
        errorVisible: false,
        errorMessage: undefined,
      });
    },
  };

  const groupState = useGroupState(groupProps);

  const { radioGroupProps, labelProps, descriptionProps, errorMessageProps } =
    useRadioGroup(groupProps, groupState);

  const items = options.filter(Boolean).map((radio, index, { length }) => {
    const position =
      length === 1
        ? "start end"
        : index === 0
          ? "start"
          : index === length - 1
            ? "end"
            : "middle";
    return (
      <RadioButton
        key={index}
        value={radio.value}
        label={radio.label}
        description={radio.description}
        icon={radio.icon}
        position={position}
        state={groupState}
        variant={variant}
        labelAlign={labelAlign}
      />
    );
  });

  return (
    <div {...radioGroupProps}>
      {label && (
        <div
          {...labelProps}
          css={{
            color: uiColors.text,
            ...typo({
              level: "body2",
              density: "tight",
              weight: "medium",
            }),
            display: "inline-grid",
            alignItems: "center",
            height: "56px",
            paddingInlineStart: "16px",
          }}
        >
          {label}
        </div>
      )}
      <div css={{ display: "flex", flexDirection: "column", rowGap: "2px" }}>
        {items}
      </div>
      {state.errorVisible && state.errorMessage ? (
        <FieldErrorMessage errorMessageProps={errorMessageProps}>
          {state.errorMessage}
        </FieldErrorMessage>
      ) : (
        description && (
          <FieldDescription descriptionProps={descriptionProps}>
            {description}
          </FieldDescription>
        )
      )}
    </div>
  );
};

const RadioButtonCheckInternal = ({
  isSelected,
  isDisabled,
  inputProps,
  inputRef,
}: {
  isSelected: boolean;
  isDisabled: boolean;
  inputProps: InputHTMLAttributes<HTMLInputElement>;
  inputRef: RefObject<HTMLInputElement>;
}) => {
  const uiColors = useUiColors();

  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <div
      css={{
        position: "relative",
        display: "grid",
        height: "32px",
        width: "32px",
        backgroundColor: isSelected
          ? uiColors.controlMain
          : uiColors.controlMinor,
        ...cssFns.placeContent("center", "center"),
        ...cssFns.boxShadow(
          !isSelected && {
            inset: true,
            offsetX: "0px",
            offsetY: "2px",
            blurRadius: "3px",
            color: cssFns.setOpacity(uiColors.everBack, 0.05),
          },
          isFocusVisible && {
            spreadRadius: "2px",
            color: uiColors.text,
          },
        ),
        ...cssFns.padding("0"),
        ...cssFns.border({ radius: "100px" }),
      }}
    >
      <input
        {...mergeProps(inputProps, focusProps)}
        css={{
          width: "100%",
          height: "100%",
          position: "absolute",
          opacity: "0",
          cursor: isDisabled ? "default" : "pointer",
          ...cssFns.margin("0"),
        }}
        ref={inputRef}
      />
      {isSelected && <icons.Check color={uiColors.textOnControlMain} />}
    </div>
  );
};

const RadioButton = ({
  value,
  label,
  description,
  icon,
  position,
  state,
  variant = "fill",
  labelAlign = "start",
}: {
  value: string;
  label: string;
  description?: string;
  icon?: ReactElement;
  position: "start" | "middle" | "end" | "start end";
  state: AriaRadioGroupState;
  variant?: "fill" | "ghost";
  labelAlign?: "start" | "end";
}) => {
  const uiColors = useUiColors();

  const descriptionId = useId();

  const inputRef = useRef<HTMLInputElement>(null);
  const { inputProps, isSelected, isDisabled } = useRadio(
    { value, "aria-label": label, "aria-describedby": descriptionId },
    state,
    inputRef,
  );

  return (
    <div
      css={{
        display: "grid",
        wordBreak: "break-word",
        opacity: isDisabled ? "0.5" : "1",
      }}
    >
      <label
        css={{
          display: "inline-grid",
          justifyContent: labelAlign === "start" ? "space-between" : "start",
          alignItems: "center",
          gridAutoFlow: "column",
          columnGap: "16px",
          backgroundColor:
            variant === "fill"
              ? uiColors.controlMinor
              : cssFns.transparentize(uiColors.everBack, 1),
          boxSizing: "border-box",
          cursor: isDisabled ? "default" : "pointer",
          ...cssFns.padding("12px", "16px"),
          ...(() => {
            switch (position) {
              case "start":
                return {
                  borderStartStartRadius: "16px",
                  borderStartEndRadius: "16px",
                };
              case "middle":
                return {};
              case "end":
                return {
                  borderEndStartRadius: "16px",
                  borderEndEndRadius: "16px",
                };
              case "start end":
              default:
                return cssFns.border({ radius: "16px" });
            }
          })(),
        }}
      >
        {labelAlign === "end" && (
          <RadioButtonCheckInternal
            isDisabled={isDisabled}
            isSelected={isSelected}
            inputProps={inputProps}
            inputRef={inputRef}
          />
        )}
        <div
          css={{
            display: "grid",
            alignItems: "center",
            gridTemplateColumns: icon ? "auto auto" : "auto",
          }}
        >
          {icon && (
            <span
              css={{
                width: "24px",
                height: "24px",
                paddingInlineEnd: "16px",
              }}
            >
              {icon}
            </span>
          )}
          <div
            css={{
              display: "grid",
            }}
          >
            {
              <ToggleLabelContent
                label={label}
                description={description}
                descriptionId={descriptionId}
              />
            }
          </div>
        </div>
        {labelAlign === "start" && (
          <RadioButtonCheckInternal
            isDisabled={isDisabled}
            isSelected={isSelected}
            inputProps={inputProps}
            inputRef={inputRef}
          />
        )}
      </label>
    </div>
  );
};

export const createRadioGroupState = <Value extends string>(
  defaultValue?: RadioGroupState<Value>,
): RadioGroupState<Value> => {
  return {
    value: undefined,
    errorVisible: false,
    errorMessage: undefined,
    invalid: false,
    ...defaultValue,
  };
};

export const useRadioGroupState = <Value extends string>(
  defaultValue?: RadioGroupState<Value>,
) => {
  return useState<RadioGroupState<Value>>(createRadioGroupState(defaultValue));
};

const validateRadioGroup = <Value extends string>({
  value,
  required,
}: {
  value?: Value;
  required: boolean;
}): InvalidRules => {
  const invalid: InvalidRules = {
    required: Boolean(required && !value),
  };

  return invalid;
};

const useErrorMessage = () => {
  const message = useMessage();

  return (invalidRules: InvalidRules) => {
    if (invalidRules.required) {
      return message({
        id: "0a5deaa9-645e-4047-acdb-4913a9833c53",
        context:
          "RadioGroup. Error message in platform libraries in the RadioGroup component.",
        default: "Required field",
      });
    }

    return undefined;
  };
};
